import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { InputText } from 'primereact/inputtext';
import React, { forwardRef, useContext, useEffect, useRef } from 'react';
import InputField from '../../../../../../components/inputField/InputField';
import { errosTratados } from '../../../../../../config/Axios';
import Pages from '../../../../../../config/Pages';
import { ApplicationContext } from '../../../../../../config/Router';
import PageMessages from '../../../../../../config/translations/PageMessages';
import Shareds from '../../../../../../config/translations/Shareds';
import Modal from '../../../../../../core/components/Modal';
import InputTextLengthCounter from '../../../../../../core/components/inputs/inputTextLengthCounter/InputTextLengthCounter';
import BaseForm from '../../../../../../core/page/components/baseForm/BaseForm';
import { arrayToDate } from '../../../../../../core/utils/DateMixins';
import { assignRefs, printToast } from '../../../../../../core/utils/Global';
import { isEmpty } from '../../../../../../core/utils/ValidForm';
import metaService from '../../../../../../service/MetaService';
import { metaInputs } from '../../../PageInputs';
import styles from './ModalMetas.module.scss';

function ModalMetas({ onSave, confirmRef, ...props }, ref) {
    const modalRef = useRef(null);
    const metaRef = useRef(null);
    const formRef = useRef(null);
    const {
        id: idIndicador,
        periodicidadeIndicador,
        unidadeMedida,
    } = useContext(ApplicationContext);

    const Inputs = ({ formik, formController }) => (
        <form onSubmit={formik.handleSubmit} className={`p-grid p-fluid p-col-12 ${styles.form}`}>
            <InputField id="descMeta" formController={formController} formik={formik} cols="12 2">
                <InputTextLengthCounter maxLen={20} ref={metaRef} showInDisable>
                    <InputText
                        value={formik.values.descMeta}
                        onChange={formik.handleChange}
                        disabled={formik.values.view}
                    />
                </InputTextLengthCounter>
            </InputField>
            <InputField
                id="dataPrevisaoApuracao"
                formController={formController}
                formik={formik}
                cols="12 3"
            >
                <Calendar
                    value={formik.values.dataPrevisaoApuracao}
                    onChange={formik.handleChange}
                    dateFormat="dd/mm/yy"
                    locale="ptBr"
                    disabled={formik.values.view}
                />
            </InputField>
            <InputField
                id="unidadeMedidaMeta"
                formController={formController}
                formik={formik}
                cols="12 3"
            >
                <InputText
                    disabled
                    onChange={formik.handleChange}
                    value={formik.values.unidadeMedidaMeta?.descUnidadeMedida}
                />
            </InputField>
            <InputField
                id="periodicidadeMeta"
                formController={formController}
                formik={formik}
                cols="12 2"
            >
                <InputText
                    disabled
                    onChange={formik.handleChange}
                    value={formik.values.periodicidadeMeta?.siglaPeriodicidade}
                />
            </InputField>
            <InputField id="situacao" formController={formController} formik={formik} cols="12 2">
                <InputText disabled onChange={formik.handleChange} value={formik.values.situacao} />
            </InputField>
            {!formik.values.view && (
                <div className={`buttonsRight ${styles.containerButtons}`}>
                    <Button
                        label="Salvar"
                        className="p-button-primary"
                        type="button"
                        icon="pi pi-check"
                        onClick={async () => {
                            formController.addCountClick();
                            const error = await formik.validateForm();
                            if (Object.keys(error).length === 0) {
                                try {
                                    await metaService.salvar({
                                        registro: formik.values,
                                        formController,
                                        id: formik.values.id,
                                    });
                                    formController.setCountClick(0);

                                    printToast({
                                        severity: 'success',
                                        summary: Pages.sei.metasResultados.title ?? '',
                                        detail: isEmpty(formik.values.id)
                                            ? Shareds.form.successCreate
                                            : Shareds.form.successUpdate,
                                    });

                                    if (onSave) {
                                        onSave();
                                    }
                                } catch (error) {
                                    if (Array.isArray(error?.response?.data?.errors)) {
                                        error?.response?.data?.errors.forEach((error) => {
                                            formik.setFieldError(error.field, error.message);
                                        });
                                    } else {
                                        formController.setCountClick(0);
                                        if (
                                            error?.response &&
                                            !errosTratados.includes(error?.response?.status)
                                        ) {
                                            printToast({
                                                severity: 'error',
                                                summary: Pages.sei.metasResultados.title ?? '',
                                                detail: error?.response,
                                            });
                                        }
                                    }
                                }
                            }
                        }}
                    />
                    <Button
                        label="Enviar Para Análise Negocial"
                        className="p-button-info"
                        type="button"
                        icon="pi pi-sign-in"
                        onClick={async () => {
                            formController.addCountClick();
                            const error = await formik.validateForm();
                            if (Object.keys(error).length === 0) {
                                confirmRef.current.show(
                                    PageMessages.sei.metasResultados.confirmEnvio,
                                    async () => {
                                        formRef.current.formik.submitForm();
                                    }
                                );
                            }
                        }}
                    />
                </div>
            )}
        </form>
    );

    const ModalBody = ({ modalData }) => {
        const { id, view = false } = modalData;

        useEffect(() => {
            if (!isEmpty(id)) {
                formRef.current.onLoad();
            }
        }, [id]);

        return (
            <>
                <BaseForm
                    ref={formRef}
                    onBuscar={() => metaService.buscarPorId({ id })}
                    onSubmit={async (data) => {
                        const response = await metaService.salvar(data);
                        onSave(true);
                        return metaService.enviarAnalise(response);
                    }}
                    actionOnSave={({ success }) => {
                        if (success && onSave) {
                            onSave();
                        }
                    }}
                    redirectOnSave={false}
                    buscarOnLoad={false}
                    initialValues={metaInputs.filter((el) => el?.form?.use ?? false)}
                    prepareInitialValues={async ({
                        situacao,
                        periodicidadeMeta,
                        unidadeMedidaMeta,
                        dataPrevisaoApuracao,
                        ...data
                    }) => {
                        setTimeout(() => metaRef.current.focus(), 500);

                        return {
                            ...data,
                            periodicidadeMeta: isEmpty(id)
                                ? periodicidadeIndicador
                                : periodicidadeMeta,
                            unidadeMedidaMeta: isEmpty(id) ? unidadeMedida : unidadeMedidaMeta,
                            situacao: isEmpty(id) ? 'Cadastro' : situacao?.descSituacaoMeta ?? '',
                            dataPrevisaoApuracao: Array.isArray(dataPrevisaoApuracao)
                                ? arrayToDate(dataPrevisaoApuracao)
                                : null,
                            indicador: { id: idIndicador },
                            view,
                        };
                    }}
                >
                    <Inputs />
                </BaseForm>
            </>
        );
    };

    return (
        <Modal
            header={
                <>
                    <i className={`pi pi-chart-line ${styles.iconHeader}`} />
                    Cadastro de Metas
                </>
            }
            style={{ maxWidth: '90vw' }}
            ref={assignRefs(ref, modalRef)}
            {...props}
        >
            <ModalBody />
        </Modal>
    );
}

export default forwardRef(ModalMetas);
